import { useCallback, useEffect, useState } from 'react';
import { useElena } from '../state';
import { holdMicForWake } from './listen';
import { session } from './session';
import { acknowledge, stopSpeakingEverywhere, warmAcknowledge } from './speech';
import { WakeListener, type WakeState } from './wake';

export interface Wake {
  enabled: boolean;
  state: WakeState;
  message: string | null;
  setEnabled: (on: boolean) => void;
  /** What the recognizer last heard, and its last retried problem: shown in the widget. */
  heard: string | null;
  issue: string | null;
}

/**
 * Keeps the "Hej Elena" listener running while it is turned on. A phrase with a command
 * opens the mic with those words already in (so you can finish the sentence); the name alone
 * opens the mic for the next sentence.
 */
export function useWake(
  onPrompt: (seed?: string, fromWake?: boolean) => void,
  always = false,
): Wake {
  const { connection, language } = useElena();
  const [saved, setEnabledState] = useState(session.wakeWord);
  const enabled = always || saved;
  const [state, setState] = useState<WakeState>('off');
  const [message, setMessage] = useState<string | null>(null);
  const [heard, setHeard] = useState<string | null>(null);
  const [issue, setIssue] = useState<string | null>(null);

  const setEnabled = useCallback((on: boolean) => {
    session.setWakeWord(on);
    setEnabledState(on);
    setMessage(null);
  }, []);

  const online = connection === 'online';
  useEffect(() => {
    if (!enabled || !online) {
      setState(enabled ? 'paused' : 'off');
      return;
    }
    const listener = new WakeListener(
      {
        // "Hej Elena, pusti..." keeps listening until you stop talking, then sends it all
        onWake: (command) => {
          // she stops talking at once, says a short "Mhm?" so you know she heard, and listens
          stopSpeakingEverywhere();
          void acknowledge();
          onPrompt(command, true);
        },
        onHeard: (text) => {
          setHeard(text);
          setIssue(null);
        },
        onIssue: setIssue,
        onState: (s, m) => {
          setState(s);
          if (s === 'error') setMessage(m ?? null);
        },
      },
      // always-on = the PC widget: it takes "Hej Elena" over from a tab
      always,
    );
    listener.start();
    warmAcknowledge();
    // the sentence that wakes Elena stays in memory for the real recognizer
    const releaseMic = holdMicForWake();
    return () => {
      listener.stop();
      releaseMic();
    };
    // a new conversation language restarts the listener in it
  }, [enabled, online, onPrompt, language, always]);

  return { enabled, state, message, setEnabled, heard, issue };
}

export const wakeLabel = (w: Wake): string =>
  !w.enabled
    ? 'isključeno'
    : w.state === 'listening'
      ? 'slušam'
      : w.state === 'error'
        ? 'ne radi'
        : 'pauza';
